import { apiClient, withAuthHeaders } from "./apiClient";

const buildSearchPath = (resource, keySearch = "") => {
  const keyword = keySearch.trim();
  return keyword ? `${resource}?key=${encodeURIComponent(keyword)}` : resource;
};

export const toeicApi = {
  async getAnswers(keySearch = "") {
    const res = await apiClient.get(buildSearchPath("answer", keySearch));
    return res.data || [];
  },

  async getAnswer(id) {
    const res = await apiClient.get(`answer/find/${id}`);
    return res.data;
  },

  async deleteAnswer({ accessToken, userID, id, axiosJWT }) {
    await axiosJWT.delete(`answer/${id}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
  },

  async getDocuments(keySearch = "") {
    const res = await apiClient.get(buildSearchPath("document", keySearch));
    return res.data || [];
  },

  async getDocument(id) {
    const res = await apiClient.get(`document/find/${id}`);
    return res.data;
  },

  async deleteDocument({ accessToken, userID, id, axiosJWT }) {
    await axiosJWT.delete(`document/${id}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
  },

  async getUsers(keySearch = "") {
    const res = await apiClient.get(buildSearchPath("user", keySearch));
    return res.data;
  },

  async getUser(userID) {
    const res = await apiClient.get(`user/find/${userID}`);
    return res.data;
  },

  async banUser({ accessToken, userID, deleteID, axiosJWT }) {
    const res = await axiosJWT.delete(`user/${deleteID}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async getActiveSliders() {
    const res = await apiClient.get("slider/find-active");
    return res.data || [];
  },

  async getSlider(id) {
    const res = await apiClient.get(`slider/find/${id}`);
    return res.data;
  },

  async getCollections(keySearch = "") {
    const resource = keySearch.trim()
      ? `slider/collection?key=${encodeURIComponent(keySearch.trim())}`
      : "slider/collection";
    const res = await apiClient.get(resource);
    return res.data || [];
  },

  async getSlidersByCollection(collection) {
    const res = await apiClient.get(`slider/collection/${collection}`);
    return res.data || [];
  },

  async toggleSlider({ accessToken, slider, axiosJWT }) {
    const res = await axiosJWT.post(`slider/toggle`, slider, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken),
    });
    return res.data;
  },

  async deleteSlider({ accessToken, id, axiosJWT }) {
    await axiosJWT.delete(`slider/${id}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken),
    });
  },

  async deleteCollection({ accessToken, collection, axiosJWT }) {
    await axiosJWT.delete(`slider/collection/${collection}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken),
    });
  },

  async getHistoryOfUser(userID) {
    const res = await apiClient.get(`history/get-of-user/${userID}`);
    return res.data || [];
  },

  async getHighestOfUser(userID) {
    const res = await apiClient.get(`history/highest/${userID}`);
    return res.data;
  },

  async getRankingListOfTest(answerID) {
    const res = await apiClient.get(`history/find-top-single/${answerID}`);
    return res.data || [];
  },

  async getComments(answerID) {
    const res = await apiClient.get(`comment/answer/${answerID}`);
    return res.data?.metadata || [];
  },

  async deleteComment({ accessToken, userID, data, axiosJWT }) {
    await axiosJWT.delete("comment", {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
      data,
    });
  },

  async getPublicFlashcardDecks(keySearch = "") {
    const res = await apiClient.get(buildSearchPath("flashcard/deck", keySearch));
    return res.data || [];
  },

  async getFlashcardWordSuggestions(keySearch = "") {
    const keyword = keySearch.trim();
    if (!keyword) return [];

    const res = await apiClient.get(
      `flashcard/lexicon/suggest?key=${encodeURIComponent(keyword)}`
    );
    return res.data || [];
  },

  async lookupFlashcardWord(word = "") {
    const keyword = word.trim();
    if (!keyword) return null;

    const res = await apiClient.get(
      `flashcard/lexicon/lookup?word=${encodeURIComponent(keyword)}`
    );
    return res.data;
  },

  async getMyFlashcardDecks({ accessToken, userID, axiosJWT, keySearch = "" }) {
    const res = await axiosJWT.get(buildSearchPath("flashcard/mine/deck", keySearch), {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data || [];
  },

  async getFlashcardCards({ deckID, accessToken, userID, axiosJWT, isMine = false }) {
    const path = isMine
      ? `flashcard/mine/deck/${deckID}/card`
      : `flashcard/deck/${deckID}/card`;

    if (isMine) {
      const res = await axiosJWT.get(path, {
        baseURL: apiClient.defaults.baseURL,
        headers: withAuthHeaders(accessToken, userID),
      });
      return res.data || [];
    }

    const res = await apiClient.get(path);
    return res.data || [];
  },

  async createFlashcardDeck({ accessToken, userID, axiosJWT, deck }) {
    const res = await axiosJWT.post("flashcard/deck", deck, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async updateFlashcardDeck({ accessToken, userID, axiosJWT, deckID, update }) {
    const res = await axiosJWT.patch(`flashcard/deck/${deckID}`, update, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async deleteFlashcardDeck({ accessToken, userID, axiosJWT, deckID }) {
    const res = await axiosJWT.delete(`flashcard/deck/${deckID}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async createFlashcardCard({ accessToken, userID, axiosJWT, deckID, card }) {
    const res = await axiosJWT.post(`flashcard/deck/${deckID}/card`, card, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async deleteFlashcardCard({ accessToken, userID, axiosJWT, cardID }) {
    const res = await axiosJWT.delete(`flashcard/card/${cardID}`, {
      baseURL: apiClient.defaults.baseURL,
      headers: withAuthHeaders(accessToken, userID),
    });
    return res.data;
  },

  async reviewFlashcardCard({ accessToken, userID, axiosJWT, cardID, remembered }) {
    const res = await axiosJWT.post(
      `flashcard/card/${cardID}/review`,
      { remembered },
      {
        baseURL: apiClient.defaults.baseURL,
        headers: withAuthHeaders(accessToken, userID),
      }
    );
    return res.data;
  },
};
